import React, { useEffect, useMemo, useRef } from 'react';
import { Col, Layout, Row } from 'tea-component';
import HashtagInfo from './component/HashtagInfo';
import HashtagTrend from './component/HashtagTrend';
import './index.scss';
import HashtagCountryDistribution from './component/HashtagCountryDistribution';
import CardLoading from '@op/components/CardLoading';
import CardCommon from '@op/components/CardCommon';
import { useFilterFromShortId } from '@opm/utils/history';
import { HashtagDetailPoint } from '@opm/operationLog/pages';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { useTranslateHashtagCategory } from '../hooks';
import { FilterDeepContext } from '@opm/components/FilterCommon/state';
import { useHashtagDetails } from '@opm/pages/Hashtag/hooks';
import HashtagDetailsWrapper from './component/HashtagDetailsWrapper';
import moment from 'moment';

const { Content } = Layout;

const HashtagDetails = () => {
  DBOM_POINT.useInitPage({
    buriedPoint: HashtagDetailPoint,
    triggerPagePoint: false,
  });
  const { filter, loading } = useFilterFromShortId();
  const { backCondConfig, translateCategory } = useTranslateHashtagCategory({
    type: 'hashtag',
  });

  const wholePageRef = useRef<HTMLDivElement>(null);
  const { trendFilter, detailFilter } = useMemo(() => {
    if (!filter) return {};

    const {
      country,
      source,
      hashtag,
      time_range: timeRange,
      start_time: startTime,
      end_time: endTime,
      date_type: dateType,
    } = filter;

    // 计算时间范围的辅助函数
    const calculateTimeRange = (endTime: string, timeRange: string) => {
      const endDate = moment(endTime, 'YYYY-MM-DD');

      const timeRangeMap: Record<string, () => string> = {
        last_7_days: () =>
          endDate
            .clone()
            .subtract(6, 'days')
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss'),
        last_30_days: () =>
          endDate
            .clone()
            .subtract(29, 'days')
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss'),
      };

      return timeRangeMap[timeRange]?.() || startTime;
    };

    const calculatedStartTime = calculateTimeRange(endTime, timeRange);

    const obj = {
      countries: country ? [country] : [],
      categories: source ? [source] : [],
      end_time: endTime,
      hashtag,
      time_range: timeRange,
      date_type: dateType,
    };

    return {
      trendFilter: {
        ...obj,
        start_time: startTime,
      },
      detailFilter: {
        ...obj,
        start_time: calculatedStartTime,
      },
    };
  }, [filter]);

  // 提升 useHashtagDetails 到父组件，直接从 hook 获取 firstAvailableTime
  const {
    data: hashtagData,
    loading: hashtagLoading,
    firstAvailableTime,
  } = useHashtagDetails({
    filter: detailFilter,
  });

  useEffect(() => {
    if (filter.hashtag) {
      DBOM_POINT.page(filter.hashtag);
    }
  }, [filter]);

  return (
    <Content className="dbom-hashtag-details" ref={wholePageRef}>
      <CardLoading loading={loading}>
        <Content.Header
          title={detailFilter?.hashtag}
          showBackButton
          onBackButtonClick={() => {
            window.history.back();
          }}
        />
        <FilterDeepContext.Provider value={{ backCondConfig }}>
          <Content.Body full>
            <HashtagInfo
              translateCategory={translateCategory}
              data={hashtagData}
              loading={hashtagLoading}
              filter={detailFilter}
            />
            <CardCommon
              loading={hashtagLoading}
              loadingHeight={700}
              emptyHeight={700}
              empty={!hashtagData || Object.keys(hashtagData).length === 0}
              contentCard
            >
              <Row className="dbom-hashtag-details-row">
                <Col span={17}>
                  <HashtagTrend
                    filter={trendFilter}
                    firstAvailableTime={firstAvailableTime}
                  />
                </Col>
                <Col span={7}>
                  <HashtagCountryDistribution filter={filter} />
                </Col>
              </Row>
              <HashtagDetailsWrapper
                translateCategory={translateCategory}
                filter={detailFilter}
                wholePageRef={wholePageRef}
              />
            </CardCommon>
          </Content.Body>
        </FilterDeepContext.Provider>
      </CardLoading>
    </Content>
  );
};

export default HashtagDetails;
